import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Box,
	Button,
	Paper,
	Typography,
	CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import Pagination from "@mui/material/Pagination";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { useCustomToast } from "../../Methods/SnackBar";
import { getProjectEffortData, jiraChartResponseProfile } from "../../../Service/Apis";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { jiraChartBlobDownload } from "../chartExport";
import { RootState } from "../../../store/Store";
import ProjectSprintFilter from "../ProjectFilter/ProjectSprintFilter";

const ITEMS_PER_PAGE = 3;
const CHART_HEIGHT = 400;
const PROJECT_COLORS: { [key: string]: string } = {};

const DELIMITER = "::";

function hashStringToColor(str: string): string {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	const h = Math.abs(hash) % 360;
	return `hsl(${h}, 65%, 50%)`;
}

const getProjectColor = (key: string) => {
	if (!PROJECT_COLORS[key]) {
		PROJECT_COLORS[key] = hashStringToColor(key);
	}
	return PROJECT_COLORS[key];
};

export type ProjectEffortChartProps = {
	projectId?: string;
	embedded?: boolean;
};

const ProjectEffortChart: React.FC<ProjectEffortChartProps> = ({
	projectId,
	embedded = false,
}) => {
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [projectData, setProjectData] = useState<any>([]);
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const [month, setMonth] = useState<string>("1");
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
	const [selectedSprints, setSelectedSprints] = useState<string[]>([]);
	const [page, setPage] = useState(1);
	const showToast = useCustomToast();

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(() => {
		const p: Record<string, string> = { ...jiraChartResponseProfile, role: nrole ?? "Employee",};
		if (projectId) {
			p.project_id = projectId;
		}
		return p;
	}, [nrole, projectId]);

	const fetchProjectData = useCallback(
		async (selectedMonth: string) => {
			try {
				setLoading(true);
				const res = await dispatch2(
					fetchData(`${getProjectEffortData}${selectedMonth}`, paramsData)
				);
				setMonth(selectedMonth);
				setProjectData(Array.isArray(res) ? res : []);
			} catch (error) {
				console.error(error);
			} finally {
				setLoading(false);
			}
		},
		[dispatch2, paramsData]
	);

	useEffect(() => {
		fetchProjectData("1");
	}, [fetchProjectData]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchProjectData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const onDownload = async () => {
		try {
			setDownloading(true);
			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;
			await jiraChartBlobDownload(
				`${getProjectEffortData}${month}`,
				{ ...paramsData, download: selectedFormat } as Record<
					string,
					unknown
				>,
				`project_effort.${ext}`,
				showToast
			);
		} finally {
			setDownloading(false);
		}
	};

	const getAvailableProjects = (data: any[]) => {
		return data.map((project) => project.project_name);
	};

	const getAvailableSprints = (data: any[]) => {
		const sprints = new Set<string>();
		data.forEach((project) => {
			project.sprints.forEach((sprint: any) => {
				sprints.add(sprint.sprint_name);
			});
		});
		return Array.from(sprints);
	};

	useEffect(() => {
		if (projectData.length > 0) {
			const projects = getAvailableProjects(projectData);
			const sprints = getAvailableSprints(projectData);
			setSelectedProjects(projects);
			setSelectedSprints(sprints);
		}
	}, [projectData]);

	const filteredData = React.useMemo(() => {
		interface Sprint {
			sprint_name: string;
			total_issues?: number;
			with_story_points?: number;
			without_story_points?: number;
			total_hours?: number;
		}

		interface Project {
			project_name: string;
			sprints: Sprint[];
		}

		return projectData
			.filter((project: Project) =>
				selectedProjects.includes(project.project_name)
			)
			.map(
				(project: Project): Project => ({
					...project,
					sprints: project.sprints.filter((sprint: Sprint) =>
						selectedSprints.includes(sprint.sprint_name)
					),
				})
			);
	}, [projectData, selectedProjects, selectedSprints]);

	const transformDataForChart = (data: any[]) => {
		const allSprints = new Set<string>();
		data.forEach((project) => {
			project.sprints.forEach((sprint: any) => {
				allSprints.add(sprint.sprint_name);
			});
		});

		return Array.from(allSprints).map((sprintName) => {
			const dataPoint: any = { sprint_name: sprintName };
			data.forEach((project) => {
				const sprint = project.sprints.find(
					(s: any) => s.sprint_name === sprintName
				);
				if (sprint) {
					dataPoint[`${project.project_name}${DELIMITER}total_issues`] =
						sprint.total_issues || 0;
					dataPoint[`${project.project_name}${DELIMITER}with_points`] =
						sprint.with_story_points || 0;
					dataPoint[`${project.project_name}${DELIMITER}without_points`] =
						sprint.without_story_points || 0;
					dataPoint[`${project.project_name}${DELIMITER}time_spent`] =
						sprint.total_time_spent ? Math.round(sprint.total_time_spent) : 0;
				}
			});
			return dataPoint;
		});
	};

	const METRIC_LABELS: Record<string, string> = {
		time_spent: " hours spent",
		with_points: " issues with story points",
		without_points: " issues without story points",
		total_issues: " total issues",
	};

	const EffortTooltip = ({ active, payload, label }: any) => {
		if (!active || !payload?.length) return null;

		// Sort payload by value (desc) then by name (alphabetically)
		const sortedPayload = [...payload].sort((a, b) => {
			const valueA = parseFloat(a?.value?.toString()) || 0;
			const valueB = parseFloat(b?.value?.toString()) || 0;
			
			// First sort by value (descending)
			if (valueB !== valueA) {
				return valueB - valueA;
			}
			
			// Then sort alphabetically by name
			const nameA = String(a?.name || "").toLowerCase();
			const nameB = String(b?.name || "").toLowerCase();
			return nameA.localeCompare(nameB);
		});

		return (
			<Box
				sx={{
					bgcolor: "#fff",
					p: "12px 16px",
					border: "1px solid #e0e0e0",
					borderRadius: "8px",
					boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
					maxHeight: 300,
					overflowY: "auto",
					maxWidth: 400,
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
					{label}
				</Typography>
				{sortedPayload.map((entry: any) => {
					const delimIdx = entry.dataKey.indexOf(DELIMITER);
					const projectName =
						delimIdx !== -1
							? entry.dataKey.slice(0, delimIdx)
							: entry.dataKey;
					const metricType =
						delimIdx !== -1
							? entry.dataKey.slice(delimIdx + DELIMITER.length)
							: "";

					return (
						<Box
							key={entry.dataKey}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "6px",
								py: "2px",
							}}
						>
							<Box
								sx={{
									width: 8,
									height: 8,
									borderRadius: "50%",
									bgcolor: entry.color,
									flexShrink: 0,
								}}
							/>
							<Typography
								variant="body2"
								sx={{ color: "#333", fontSize: "12px" }}
							>
								{projectName}: {entry.value}
								{METRIC_LABELS[metricType] || ""}
							</Typography>
						</Box>
					);
				})}
			</Box>
		);
	};

	const paginatedData = useMemo(() => {
		const chartData = transformDataForChart(filteredData);
		const start = (page - 1) * ITEMS_PER_PAGE;
		return chartData.slice(start, start + ITEMS_PER_PAGE);
	}, [filteredData, page]);

	const handlePageChange = (
		event: React.ChangeEvent<unknown>,
		value: number
	) => {
		setPage(value);
	};

	const chartBars = useMemo(() => {
		return selectedProjects.map((projectName: string) => (
			<React.Fragment key={projectName}>
				<Bar
					dataKey={`${projectName}${DELIMITER}with_points`}
					name={`${projectName} (With Points)`}
					fill={getProjectColor(projectName)}
					barSize={60}
					maxBarSize={80}
				/>
				<Bar
					dataKey={`${projectName}${DELIMITER}without_points`}
					name={`${projectName} (Without Points)`}
					fill={getProjectColor(`${projectName}_without`)}
					barSize={60}
					maxBarSize={80}
				/>
				<Bar
					dataKey={`${projectName}${DELIMITER}time_spent`}
					name={`${projectName} (Time Spent)`}
					fill={getProjectColor(`${projectName}_time`)}
					barSize={60}
					maxBarSize={80}
				/>
			</React.Fragment>
		));
	}, [selectedProjects, paginatedData.length]);

	return (
		<Paper sx={embedded ? { m: 0, mb: 2, p: 2 } : { m: 2, p: 4 }}>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					flexDirection="column"
					gap={2}
				>
					<Typography variant="h4">Project Effort Analysis</Typography>
					<MonthButtonGroup buttonData={buttonData} />
				</Box>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					flexDirection="column"
					gap={2}
				>
					{!projectId ? (
						<Box mb={3}>
							<ProjectSprintFilter
								projects={getAvailableProjects(projectData)}
								selectedProjects={selectedProjects}
								sprints={getAvailableSprints(projectData)}
								selectedSprints={selectedSprints}
								onProjectChange={setSelectedProjects}
								onSprintChange={setSelectedSprints}
								projectData={projectData}
							/>
						</Box>
					) : null}
					{downloading ? (
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							gap={2}
						>
							<CircularProgress size="1rem" />
							<Typography>Downloading...</Typography>
						</Box>
					) : (
						<Button
							variant="outlined"
							size="small"
							onClick={onDownload}
						>
							Export
						</Button>
					)}
				</Box>
			</Box>
			{loading ? (
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					height={400}
				>
					<CircularProgress />
				</Box>
			) : (
				<>
					<ResponsiveContainer width="100%" height={CHART_HEIGHT}>
						<BarChart data={paginatedData} barGap={5} barCategoryGap={20}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="sprint_name" />
							<YAxis />
							<Tooltip
								content={<EffortTooltip />}
								wrapperStyle={{ zIndex: 9999, pointerEvents: "auto" }}
							/>
							{chartBars}
						</BarChart>
					</ResponsiveContainer>
					<Box display="flex" justifyContent="center" mt={2}>
						<Pagination
							count={Math.ceil(
								transformDataForChart(filteredData).length / ITEMS_PER_PAGE
							)}
							page={page}
							onChange={handlePageChange}
							color="primary"
						/>
					</Box>
				</>
			)}
		</Paper>
	);
};

export default ProjectEffortChart;
